import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import * as Sentry from "@sentry/tanstackstart-react";
import { useRouter } from "@tanstack/react-router";
import { AlertCircle, ArrowLeft, Home, RefreshCw } from "lucide-react";
import { useEffect } from "react";

import { authClient } from "@/auth/auth-client";

interface ErrorPageProps {
  error?: Error;
  reset?: () => void;
}

export function ErrorPage({ error, reset }: ErrorPageProps) {
  const router = useRouter();
  const isDev = import.meta.env.DEV;

  const isUnauthorized =
    error?.message?.toLowerCase() === "unauthorized" ||
    error?.message?.includes("Unauthorized");

  useEffect(() => {
    if (error) {
      const eventId = Sentry.captureException(error);

      if (!isDev && !isUnauthorized) {
        authClient
          .getSession()
          .then(({ data }) => {
            Sentry.showReportDialog({
              eventId,
              name: data?.user?.name || undefined,
              email: data?.user?.email || undefined,
              title: "Bir şeyler ters gitti 😢",
              subtitle: "Hata geliştirici ekibimize iletildi.",
              subtitle2:
                "Ne olduğunu kısaca yazarak bize yardımcı olmak ister misiniz?",
              labelName: "Adınız",
              labelEmail: "E-posta Adresiniz",
              labelComments: "Ne yaparken bu hatayla karşılaştınız?",
              labelSubmit: "Gönder",
              labelClose: "Kapat",
              successMessage:
                "Geri bildiriminiz başarıyla iletildi. Teşekkür ederiz!",
            });
          })
          .catch(() => {
            Sentry.showReportDialog({
              eventId,
              title: "Bir şeyler ters gitti 😢",
              subtitle: "Hata geliştirici ekibimize iletildi.",
              subtitle2:
                "Ne olduğunu kısaca yazarak bize yardımcı olmak ister misiniz?",
              labelName: "Adınız",
              labelEmail: "E-posta Adresiniz",
              labelComments: "Ne yaparken bu hatayla karşılaştınız?",
              labelSubmit: "Gönder",
              labelClose: "Kapat",
              successMessage:
                "Geri bildiriminiz başarıyla iletildi. Teşekkür ederiz!",
            });
          });
      }
    }
  }, [error, isUnauthorized]);

  useEffect(() => {
    if (isUnauthorized) {
      window.location.href = "/password/login";
    }
  }, [isUnauthorized]);

  if (isUnauthorized) {
    return null;
  }

  const handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      router.navigate({ to: "/" });
    }
  };

  const handleRefresh = () => {
    if (reset) {
      reset();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-24">
      <div className="container mx-auto max-w-2xl">
        <Card className="border-2 shadow-xl">
          <CardHeader className="space-y-6 text-center">
            {/* Icon */}
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
              <AlertCircle className="h-10 w-10 text-primary" />
            </div>

            {/* Title */}
            <div className="space-y-2">
              <h1 className="font-bold text-3xl tracking-tight sm:text-4xl">
                Bir Şeyler Yanlış Gitti
              </h1>
              <p className="text-lg text-muted-foreground">
                Üzgünüz, bir hata oluştu. Lütfen sayfayı yenileyin veya ana
                sayfaya dönün.
              </p>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Error Details (Dev Only) */}
            {isDev && error && (
              <div className="rounded-lg border bg-muted/50 p-4">
                <p className="mb-2 font-semibold text-sm">
                  Geliştirici Bilgisi:
                </p>
                <p className="mb-2 font-mono text-destructive text-sm">
                  {error.message}
                </p>
                {error.stack && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-muted-foreground text-xs hover:text-foreground">
                      Stack Trace
                    </summary>
                    <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap font-mono text-xs">
                      {error.stack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <Button onClick={handleRefresh} size="lg">
                <RefreshCw className="mr-2 h-4 w-4" />
                Sayfayı Yenile
              </Button>
              <Button
                onClick={() => router.navigate({ to: "/" })}
                variant="outline"
                size="lg"
              >
                <Home className="mr-2 h-4 w-4" />
                Ana Sayfa
              </Button>
              <Button onClick={handleGoBack} variant="ghost" size="lg">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Geri Dön
              </Button>
            </div>

            {/* Help Text */}
            <p className="text-center text-muted-foreground text-sm">
              Sorun devam ederse, lütfen{" "}
              <a
                href="mailto:support@atalaythecoach.com"
                className="text-primary underline-offset-4 hover:underline"
              >
                destek ekibi
              </a>
              yle iletişime geçin.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
